/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module CportalModule
 */

import {
    Component,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';

import {
    IRegisterControllerPayLoad,
    PortalInfo,
} from 'ajs/modules/cportal';

import {
    ALBServicesConnectivityStatus,
    ALBServicesRegistrationStatus,
    IALBServicesAccount,
    IALBServicesAssetDetails,
    IALBServicesConfig,
    IALBServicesStatus,
    LicenseTierType,
} from 'generated-types';

import {
    ALBServicesConfig,
    PortalFeatureOptIn,
    WafCrsConfig,
} from 'object-types';

import {
    from,
    of,
    Subject,
} from 'rxjs';

import {
    switchMap,
    take,
    takeUntil,
} from 'rxjs/operators';

import { AlbServicesSiteCollection, TAlbServicesSiteCollection } from 'ng/modules/collections/factories';
import { ClrFormLayout } from '@clr/angular';
import { CportalService, IError } from 'ajs/modules/cportal/services';
import { createDropdownOption } from 'ng/shared/utils';
import { IAviDropdownOption } from 'standalone-components/avi-dropdown';
import { IHttpResponse, IPromise } from 'angular';
import { L10nService } from '@vmw/ngx-vip';
import { Store } from '@ngrx/store';
import * as globalL10n from 'global-l10n';
import * as SystemInfoSelectors from 'ng/root-store/system-info/system-info.selectors';

import './cportal-info-modal.component.less';
import { CPortalPageStore } from '../cportal-page';
import * as l10n from './cportal-info-modal.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description
 *      Component used to represent CPortal page.
 * @author Ram Pal, Ashish Verma, Navneet Sharma, Rajawant Prajapati, Ratan Kumar, Shahab Hashmi, Deepak Chaudhary
 */
@Component({
    selector: 'cportal-info-modal',
    templateUrl: './cportal-info-modal.component.html',
})
export class CPortalInfoModalComponent implements OnInit, OnDestroy {
    /**
     * PortalInfo item
     */
    @Input()
    public editable: PortalInfo;

    /**
     * Controller name to pass in modal.
     */
    @Input()
    public controllerName: string;

    /**
     * Method to be called after process is complete.
     */
    @Output()
    public onComplete: () => void;

    /**
     * Method to be called after save to reload store.
     */
    @Output()
    public reloadStore: () => void;

    /**
     * User Selector dropdown options.
     */
    public users: IAviDropdownOption[] = [];

    /**
     * Selected site Id.
     */
    public selectedSiteId = '';

    /**
     * Selected default contact.
     */
    public defaultContact = '';

    /**
     * True if model is open in edit mode.
     */
    public editMode = false;

    /**
     * Set to true when fetching user info and saving portal info.
     */
    public busy = false;

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly albServicesSiteCollection: AlbServicesSiteCollection;

    /**
     * Selected license tier.
     */
    public licenseTier: LicenseTierType;

    /**
     * Layout for modal.
     */
    public readonly verticalLayout = ClrFormLayout.VERTICAL;

    /**
     * cPortal info object type.
     */
    public objectType = {
        ALBServicesConfig,
        PortalFeatureOptIn,
        WafCrsConfig,
    };

    /**
     * Default variable for error message.
     */
    public error: IError;

    // eslint-disable-next-line max-len
    public LINK = 'https://techdocs.broadcom.com/us/en/vmware-security-load-balancing/avi-load-balancer/avi-load-balancer/22-1/vmware-avi-load-balancer-waf-guide/architecture/waf-policy/application-rules.html';

    /**
     * Departments belonging to user.
     */
    private departments: IALBServicesAccount[] = [];

    /**
     * Subject used to unsubscribe observables.
     */
    private readonly ngDestroyed$ = new Subject();

    private albServiceStatus: IALBServicesStatus;

    constructor(
        private readonly cPortalPageStore: CPortalPageStore,
        private readonly cPortalService: CportalService,
        private readonly l10nService: L10nService,
        @Inject(AlbServicesSiteCollection)
        AlbServicesSiteCollection: TAlbServicesSiteCollection,
        store: Store,
    ) {
        this.l10nService.registerSourceBundles(dictionary);
        this.albServicesSiteCollection = new AlbServicesSiteCollection();

        store.select(SystemInfoSelectors.selectDefaultLicenseTier)
            .pipe(take(1))
            .subscribe((tier: LicenseTierType) => this.licenseTier = tier);
    }

    /** @override */
    public ngOnInit(): void {
        this.cPortalPageStore.albServiceStatus$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((albServiceStatus: IALBServicesStatus) => {
                this.albServiceStatus = albServiceStatus;
            });

        this.cPortalPageStore.assetDetails$
            .pipe(
                takeUntil(this.ngDestroyed$),
                switchMap((assetDetails: IALBServicesAssetDetails) => {
                    this.defaultContact = assetDetails?.email;
                    this.users = assetDetails?.email ? [createDropdownOption(assetDetails?.email)] : [];

                    if (assetDetails?.site?.site_id) {
                        return from(this.albServicesSiteCollection.load()).pipe(
                            switchMap(() => of(assetDetails.site.site_id)),
                        );
                    }

                    return of(null);
                }),
            )
            .subscribe((siteId: string | null) => {
                if (siteId) {
                    this.selectedSiteId = siteId;
                }
            });
    }

    /** @override */
    public ngOnDestroy(): void {
        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();
        this.albServicesSiteCollection.destroy();
    }

    public get isUpdateGeneralSectionDisabled(): boolean {
        const {
            connectivity_status: connectivityStatus,
            registration_status: registrationStatus,
        } = this.albServiceStatus ?? {};

        return connectivityStatus === ALBServicesConnectivityStatus.ALBSERVICES_CONNECTED &&
            registrationStatus === ALBServicesRegistrationStatus.ALBSERVICES_REGISTERED;
    }

    /**
     * Saves portal info and registers controller thereafter.
     */
    public savePortalInfoAndRegisterController(): void {
        this.busy = true;
        this.error = undefined;

        let payload: IRegisterControllerPayLoad;

        if (!this.editMode) {
            payload = this.getRegisterPayload();
        }

        let promise: IPromise<IHttpResponse<IALBServicesConfig> | void> = this
            .editable.save()
            .catch((error: IError) => Promise.reject(error.data));

        // This code will run only for controller registration.
        if (!this.editMode) {
            promise = promise
                .then(() => this.cPortalService.registerController(payload))
                .then(() => {
                    this.onComplete();
                    this.reloadStore();
                });
        }

        promise
            .then(() => {
                this.reloadStore();
                this.editable.closeModal();
            })
            .catch((error: IError) => this.error = error)
            .finally(() => this.busy = false);
    }

    /**
     * Get registration request payload.
     */
    private getRegisterPayload(): IRegisterControllerPayLoad {
        return {
            name: this.controllerName,
            description: this.controllerName,
            email: this.defaultContact,
            site_id: this.selectedSiteId,
        };
    }
}
